import React, { useEffect, useState, useCallback, useMemo, lazy, Suspense } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Bell, Truck, Zap, Users, Settings, LogOut, AlertCircle, Menu, X, Trash2, CheckCheck, Search, Droplet, UserCheck, Fuel, FileSpreadsheet, ClipboardList, Home, ScrollText, Wallet, Wrench, HardHat, ClipboardCheck, FileUp, Receipt, CreditCard, Lock, CalendarDays, Inbox, AlertTriangle, ChevronDown, Eye, EyeOff, Building2, ShieldAlert, Radar, Headset, LayoutGrid, CalendarClock, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { usePersisted } from "../lib/usePersisted";
import { useAuth } from "../context/AuthContext";
import { useCostVisibility } from "../context/CostVisibility";
import { ProLock } from "../components/UpgradeToPro";
import SubscribePanel from "../components/SubscribePanel";
import { isCoordDesktop, COORD_HOME } from "../lib/desktop";
// Pagine admin caricate on-demand (code-splitting React.lazy) → bundle iniziale più piccolo, avvio del pannello più veloce.
// Loader nominati: usati sia da lazy() sia dal prefetch al hover (webpack condivide lo stesso chunk).
const loadDashboard = () => import("./admin/Dashboard");
const loadSegnalazioni = () => import("./admin/Segnalazioni");
const loadMezzi = () => import("./admin/Mezzi");
const loadMezzoDetail = () => import("./admin/MezzoDetail");
const loadCosti = () => import("./admin/Costi");
const loadOfficine = () => import("./admin/Officine");
const loadOfficinaDetail = () => import("./admin/OfficinaDetail");
const loadRegistro = () => import("./admin/Registro");
const loadMagazzinieri = () => import("./admin/Magazzinieri");
const loadDaApprovare = () => import("./admin/DaApprovare");
const loadMieiInterventi = () => import("./admin/MieiInterventi");
const loadTipoOlio = () => import("./admin/TipoOlio");
const loadAdmins = () => import("./admin/Admins");
const loadAuditLog = () => import("./admin/AuditLog");
const loadImportaDati = () => import("./admin/ImportaDati");
const loadArchivioFatture = () => import("./admin/ArchivioFatture");
const loadAbbonamento = () => import("./admin/Abbonamento");
const loadAppuntamentiBoard = () => import("./admin/AppuntamentiBoard");
const loadAziende = () => import("./admin/Aziende");
const loadRadarVoli = () => import("./admin/RadarVoli");
const loadCoordinatori = () => import("./admin/Coordinatori");
const loadOperai = () => import("./admin/Operai");
const loadPianificazione = () => import("./admin/Pianificazione");
const loadTurni = () => import("./admin/Turni");
const loadImpostazioni = () => import("./admin/Impostazioni");

const Dashboard = lazy(loadDashboard);
const Segnalazioni = lazy(loadSegnalazioni);
const Mezzi = lazy(loadMezzi);
const MezzoDetail = lazy(loadMezzoDetail);
const Costi = lazy(loadCosti);
const Officine = lazy(loadOfficine);
const OfficinaDetail = lazy(loadOfficinaDetail);
const Registro = lazy(loadRegistro);
const Magazzinieri = lazy(loadMagazzinieri);
const DaApprovare = lazy(loadDaApprovare);
const MieiInterventi = lazy(loadMieiInterventi);
const TipoOlio = lazy(loadTipoOlio);
const Admins = lazy(loadAdmins);
const AuditLog = lazy(loadAuditLog);
const ImportaDati = lazy(loadImportaDati);
const ArchivioFatture = lazy(loadArchivioFatture);
const Abbonamento = lazy(loadAbbonamento);
const AppuntamentiBoard = lazy(loadAppuntamentiBoard);
const Aziende = lazy(loadAziende);
const RadarVoli = lazy(loadRadarVoli);
const Coordinatori = lazy(loadCoordinatori);
const Operai = lazy(loadOperai);
const Pianificazione = lazy(loadPianificazione);
const Turni = lazy(loadTurni);
const Impostazioni = lazy(loadImpostazioni);

// Prefetch al hover/focus: mappa path voce di menu → loader del suo chunk.
const PREFETCH = {
  "/admin": loadDashboard,
  "/admin/richieste": loadSegnalazioni,
  "/admin/segnalazioni": loadSegnalazioni,
  "/admin/miei-interventi": loadMieiInterventi,
  "/admin/da-approvare": loadDaApprovare,
  "/admin/radar": loadRadarVoli,
  "/admin/operai": loadOperai,
  "/admin/pianificazione": loadPianificazione,
  "/admin/turni": loadTurni,
  "/admin/mezzi": loadMezzi,
  "/admin/officine": loadOfficine,
  "/admin/tipo-olio": loadTipoOlio,
  "/admin/costi": loadCosti,
  "/admin/registro": loadRegistro,
  "/admin/fatture": loadArchivioFatture,
  "/admin/magazzinieri": loadMagazzinieri,
  "/admin/coordinatori": loadCoordinatori,
  "/admin/admins": loadAdmins,
  "/admin/importa": loadImportaDati,
  "/admin/piattaforma": loadAziende,
  "/admin/log": loadAuditLog,
  "/admin/abbonamento": loadAbbonamento,
  "/admin/impostazioni": loadImpostazioni,
};
const prefetchPage = (path) => { try { PREFETCH[path]?.(); } catch (e) {} };

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard", adminOnly: true, section: "Operatività" },
  { to: "/admin/richieste", icon: Inbox, label: "Richieste", testid: "nav-richieste", key: "segnalazioni", magazOk: true, section: "Operatività" },
  { to: "/admin/segnalazioni", icon: AlertTriangle, label: "Segnalazioni", testid: "nav-segnalazioni", key: "segnalazioni", magazOk: true, section: "Operatività" },
  { to: "/admin/miei-interventi", icon: Wrench, label: "I miei interventi", testid: "nav-miei-interventi", magazOnly: true, section: "Operatività" },
  { to: "/admin/da-approvare", icon: ClipboardCheck, label: "Segnalazioni da Approvare", testid: "nav-da-approvare", adminOnly: true, approvalBadge: true, section: "Operatività" },
  { to: "/admin/radar", icon: Radar, label: "Radar Voli", testid: "nav-radar", coordOk: true, proOnly: true, section: "Coordinamento" },
  { to: "/admin/operai", icon: HardHat, label: "Operai", testid: "nav-operai", coordOk: true, proOnly: true, section: "Coordinamento" },
  { to: "/admin/pianificazione", icon: LayoutGrid, label: "Pianificazione", testid: "nav-pianificazione", coordOk: true, proOnly: true, section: "Coordinamento" },
  { to: "/admin/turni", icon: CalendarClock, label: "Turni", testid: "nav-turni", coordOk: true, proOnly: true, section: "Coordinamento" },
  { to: "/admin/mezzi", icon: Truck, label: "Mezzi", testid: "nav-mezzi", key: "mezzi", section: "Flotta" },
  { to: "/admin/officine", icon: Wrench, label: "Officine", testid: "nav-officine", key: "officine", section: "Flotta" },
  { to: "/admin/tipo-olio", icon: ClipboardList, label: "Dati tecnici", testid: "nav-dati-tecnici", key: "tipo-olio", section: "Flotta" },
  { to: "/admin/costi", icon: Wallet, label: "Costi", testid: "nav-costi", key: "costi", proOnly: true, section: "Archivi" },
  { to: "/admin/registro", icon: FileSpreadsheet, label: "Archivio", testid: "nav-registro", key: "registro", section: "Archivi" },
  { to: "/admin/fatture", icon: Receipt, label: "Archivio Fatture", testid: "nav-fatture", superadminOnly: true, section: "Archivi" },
  { to: "/admin/magazzinieri", icon: HardHat, label: "Magazzinieri", testid: "nav-magazzinieri", adminOnly: true, section: "Gestione" },
  { to: "/admin/coordinatori", icon: Headset, label: "Coordinatori", testid: "nav-coordinatori", adminOnly: true, proOnly: true, section: "Gestione" },
  { to: "/admin/admins", icon: Users, label: "Amministratori", testid: "nav-admins", adminOnly: true, section: "Gestione" },
  { to: "/admin/importa", icon: FileUp, label: "Importa dati", testid: "nav-importa", adminOnly: true, proOnly: true, section: "Sistema" },
  { to: "/admin/piattaforma", icon: Building2, label: "Piattaforma", testid: "nav-piattaforma", superadminOnly: true, section: "Sistema" },
  { to: "/admin/log", icon: ScrollText, label: "Log attività", testid: "nav-log", principalOnly: true, proOnly: true, section: "Sistema" },
  { to: "/admin/abbonamento", icon: CreditCard, label: "Abbonamento", testid: "nav-abbonamento", principalOnly: true, section: "Sistema" },
  { to: "/admin/impostazioni", icon: Settings, label: "Impostazioni", testid: "nav-impostazioni", magazOk: true, section: "Sistema" },
];

const SECTIONS = ["Operatività", "Coordinamento", "Flotta", "Archivi", "Gestione", "Sistema"];
const SECTION_META = {
  "Operatività": { color: "text-blue-300", dot: "bg-blue-500", active: "bg-blue-500/10" },
  "Coordinamento": { color: "text-sky-300", dot: "bg-sky-500", active: "bg-sky-500/10" },
  "Flotta": { color: "text-blue-300", dot: "bg-blue-500", active: "bg-blue-500/10" },
  "Archivi": { color: "text-blue-300", dot: "bg-blue-500", active: "bg-blue-500/10" },
  "Gestione": { color: "text-blue-300", dot: "bg-blue-500", active: "bg-blue-500/10" },
  "Sistema": { color: "text-blue-300", dot: "bg-blue-500", active: "bg-blue-500/10" },
};

// Tab il cui accesso è gestito dall'admin principale per ogni amministratore
export const TAB_DEFAULT = { segnalazioni: true, mezzi: true, consumi: false, costi: false, officine: false, registro: true, "tipo-olio": true };
export const canSeeTab = (user, key) => {
  if (!key) return true;
  if (user?.role === "coordinatore") return false;
  if (user?.role === "magazziniere") return key === "segnalazioni";
  if (user?.role === "principal") return true;
  const p = user?.tab_perms || {};
  return key in p ? !!p[key] : !!TAB_DEFAULT[key];
};

function isNavVisible(n, user, isMagaz, isAdmin, isCoord, coordApp) {
  if (coordApp) return !!n.coordOk || n.to === "/admin/impostazioni";
  if (n.magazOnly) return isMagaz;
  if (n.superadminOnly) return !!user?.is_superadmin;
  if (isMagaz) return !!n.magazOk;
  if (isCoord) return !!n.coordOk || n.to === "/admin/impostazioni";
  if (n.coordOnly) return false;
  if (n.principalOnly && user?.role !== "principal") return false;
  if (n.adminOnly && !isAdmin) return false;
  return canSeeTab(user, n.key);
}

export default function AdminApp() {
  const { user, logout, exitCompany } = useAuth();
  const { canToggle: canToggleCosts, revealed: costsRevealed, toggle: toggleCosts } = useCostVisibility();
  const navigate = useNavigate();
  const isMagaz = user?.role === "magazziniere";
  const isCoord = user?.role === "coordinatore";
  const isAdmin = user?.role === "principal" || user?.role === "admin";
  const coordApp = isCoordDesktop(); // app desktop "Thoth Coordinamento": solo Radar, Operai, Pianificazione, Turni
  const homePath = coordApp ? COORD_HOME : "/admin";
  const companyLogo = user?.company?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${user.company.logo_url}` : null;
  const companyBlocked = !!(user?.company?.blocked) && !user?.is_superadmin;
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [approvalCount, setApprovalCount] = useState(0);
  const [bellOpen, setBellOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarHidden, setSidebarHidden] = usePersisted("admin.sidebarHidden", false); // nascondi/mostra tendina di sinistra (desktop)
  const location = useLocation();
  const activeSection = useMemo(() => {
    const p = location.pathname;
    let best = null;
    for (const n of NAV) {
      if (n.to === "/admin") { if (p === "/admin") best = n; }
      else if (p === n.to || p.startsWith(n.to + "/")) { if (!best || n.to.length > best.to.length) best = n; }
    }
    return best?.section || "Operatività";
  }, [location.pathname]);
  const [openSecs, setOpenSecs] = useState({});
  useEffect(() => { setOpenSecs((s) => ({ ...s, [activeSection]: true })); }, [activeSection]);
  const toggleSec = (sec) => setOpenSecs((s) => ({ ...s, [sec]: !s[sec] }));
  const [scadenzeCount, setScadenzeCount] = useState(0);
  const [vehicles, setVehicles] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  const loadNotifs = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data.notifications);
      setUnread(data.unread);
    } catch {}
  }, []);

  useEffect(() => {
    if (companyBlocked) return;
    loadNotifs();
    const t = setInterval(loadNotifs, 20000);
    return () => clearInterval(t);
  }, [loadNotifs, companyBlocked]);

  const loadApproval = useCallback(async () => {
    if (!isAdmin) return;
    try { const { data } = await api.get("/reports/pending-count"); setApprovalCount(data.count || 0); } catch {}
  }, [isAdmin]);
  useEffect(() => {
    if (companyBlocked) return;
    loadApproval();
    const t = setInterval(loadApproval, 20000);
    return () => clearInterval(t);
  }, [loadApproval, companyBlocked]);

  const loadScadenze = useCallback(async () => {
    try { const { data } = await api.get("/scadenze"); const list = Array.isArray(data) ? data : []; setScadenzeCount(list.length); } catch {}
  }, []);
  const loadVehicles = useCallback(async () => {
    try { const { data } = await api.get("/vehicles"); setVehicles(data); } catch {}
  }, []);
  useEffect(() => { if (companyBlocked) return; loadScadenze(); loadVehicles(); }, [loadScadenze, loadVehicles, companyBlocked]);

  const searchResults = searchQ.trim()
    ? vehicles.filter((v) => {
        const s = searchQ.trim().toLowerCase();
        return v.targa.toLowerCase().includes(s) || (v.n_soc || "").toLowerCase().includes(s) || (v.tipo || "").toLowerCase().includes(s);
      }).slice(0, 8)
    : [];
  const goVehicle = (id) => { setSearchQ(""); setSearchOpen(false); navigate(`/admin/mezzi/${id}`); };

  const markAll = async () => { await api.post("/notifications/read-all"); loadNotifs(); };
  const delNotif = async (id) => { await api.delete(`/notifications/${id}`); loadNotifs(); };
  const clearAll = async () => { await api.delete("/notifications"); loadNotifs(); toast.success("Notifiche cancellate"); };

  const doLogout = async () => { await logout(); navigate("/admin/login"); };
  const doExitCompany = async () => { await exitCompany(); toast.success("Uscito dall'azienda"); navigate("/admin/piattaforma"); };

  if (companyBlocked) {
    return <BlockedView company={user.company} onLogout={doLogout} />;
  }

  const SidebarContent = () => (
    <>
      <div>
        <div className="flex items-center gap-2.5 px-2 mb-8">
          {companyLogo ? (
            <img src={companyLogo} alt="Logo azienda" data-testid="sidebar-company-logo" className="w-9 h-9 rounded-lg object-contain bg-white border border-slate-700 p-0.5" />
          ) : (
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-700 grid place-items-center"><Zap className="w-5 h-5" fill="currentColor" /></div>
          )}
          <div>
            <div className="font-head font-bold leading-none">{coordApp ? "Thoth Coordinamento" : "Thoth"}</div>
            <div className="text-[11px] text-slate-500 mt-0.5" data-testid="sidebar-company-name">{user?.company?.name || "Facile, veloce e sempre con te!"}</div>
          </div>
        </div>
        {coordApp && (
          <div data-testid="coord-app-badge" className="mx-2 -mt-5 mb-5 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-300">
            <Radar className="w-3 h-3" /> App desktop · Radar, Turni, Pianificazione
          </div>
        )}
        <nav className="space-y-1.5">
          {SECTIONS.map((sec) => {
            const items = NAV.filter((n) => n.section === sec && isNavVisible(n, user, isMagaz, isAdmin, isCoord, coordApp));
            if (items.length === 0) return null;
            const isOpen = !!openSecs[sec];
            const secApproval = items.some((n) => n.approvalBadge) && approvalCount > 0;
            const sm = SECTION_META[sec] || { color: "text-slate-400", dot: "bg-slate-500", active: "bg-slate-500/10" };
            return (
              <div key={sec} className="space-y-1">
                <button type="button" onClick={() => toggleSec(sec)} data-testid={`nav-section-${sec}`}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider ${sm.color} hover:${sm.active} transition-colors`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${sm.dot}`} />
                  <span className="flex-1 text-left">{sec}</span>
                  {!isOpen && secApproval && <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-500" />}
                  <ChevronDown className={`w-3.5 h-3.5 opacity-70 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                </button>
                {isOpen && items.map((n) => {
                  const isMezzi = n.to === "/admin/mezzi" && scadenzeCount > 0;
                  const showApproval = n.approvalBadge && approvalCount > 0;
                  const locked = n.proOnly && user?.company?.tier && user.company.tier !== "pro";
                  return (
                    <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid} onClick={() => setMenuOpen(false)}
                      onMouseEnter={() => prefetchPage(n.to)} onFocus={() => prefetchPage(n.to)} onTouchStart={() => prefetchPage(n.to)}
                      className={({ isActive }) => `relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${isActive ? "bg-blue-600 text-white font-semibold" : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"}`}>
                      <n.icon className="w-5 h-5 shrink-0" /> <span className="flex-1">{n.label}</span>
                      {locked && (
                        <span data-testid={`${n.testid}-pro-lock`} title="Funzione Pro" className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-amber-400/40 bg-amber-500/10 text-amber-300"><Lock className="w-3 h-3" /> Pro</span>
                      )}
                      {isMezzi && (
                        <span data-testid="mezzi-scadenze-badge" className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-5 h-5 px-1 grid place-items-center">{scadenzeCount}</span>
                      )}
                      {showApproval && (
                        <span data-testid="da-approvare-badge" className="bg-fuchsia-500 text-white text-[10px] font-bold rounded-full min-w-5 h-5 px-1 grid place-items-center">{approvalCount}</span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>
      </div>
      <div className="border-t border-slate-800 pt-3">
        <div className="px-2 mb-2">
          <div className="text-sm font-semibold">{user?.first_name} {user?.last_name}</div>
          <div className="text-[11px] text-slate-500">{user?.role === "principal" ? "Admin principale" : user?.role === "magazziniere" ? "Magazziniere" : user?.role === "coordinatore" ? "Coordinatore" : "Amministratore"}</div>
        </div>
        <button onClick={doLogout} data-testid="logout-btn" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-400 hover:bg-slate-800 hover:text-red-400 w-full">
          <LogOut className="w-5 h-5" /> Esci
        </button>
        <div className="px-3 pt-3 text-[10px] text-slate-600" data-testid="credit-dc">Creato da Danilo Carboni · Tutti i diritti riservati</div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#0B0F17] flex">
      {/* Desktop sidebar */}
      {!sidebarHidden && (
        <aside className="hidden md:flex w-64 bg-[#121926] border-r border-slate-800 p-4 flex-col justify-between shrink-0 sticky top-0 h-screen overflow-y-auto" data-testid="desktop-sidebar">
          {SidebarContent()}
        </aside>
      )}

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMenuOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-[#121926] border-r border-slate-800 p-4 flex flex-col justify-between overflow-y-auto">
            {SidebarContent()}
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <div className="sticky top-0 z-30">
        {user?.impersonating && (
          <div data-testid="impersonation-banner" className="bg-indigo-600 text-white px-4 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold min-w-0">
              <ShieldAlert className="w-4 h-4 shrink-0" />
              <span className="truncate">Stai operando come <b>Piattaforma</b> — {user?.company?.name}</span>
            </div>
            <button onClick={doExitCompany} data-testid="impersonation-exit-btn" className="shrink-0 text-xs font-bold bg-white/15 hover:bg-white/25 rounded-full px-3 py-1.5 transition-colors">Esci dall'azienda</button>
          </div>
        )}
        <header className="bg-[#0B0F17]/90 backdrop-blur border-b border-slate-800 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 shrink-0">
            <button className="md:hidden p-2 -ml-2" onClick={() => setMenuOpen(true)} data-testid="mobile-menu-btn"><Menu className="w-6 h-6" /></button>
            <button onClick={() => setSidebarHidden((v) => !v)} data-testid="sidebar-toggle-btn" title={sidebarHidden ? "Mostra il menu laterale" : "Nascondi il menu laterale"}
              className="hidden md:inline-flex p-2 rounded-lg hover:bg-slate-800 text-slate-300 hover:text-white">
              {sidebarHidden ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
            </button>
            <button onClick={() => navigate(homePath)} data-testid="header-home-btn" title={coordApp ? "Vai al Radar Voli" : "Vai alla Dashboard"} className="p-2 rounded-lg hover:bg-slate-800 text-slate-300 hover:text-white"><Home className="w-5 h-5" /></button>
            {canToggleCosts && !coordApp && (
              <button onClick={toggleCosts} data-testid="costs-visibility-toggle" title={costsRevealed ? "Nascondi i costi in tutta l'app" : "Mostra i costi in tutta l'app"}
                className={`p-2 rounded-lg hover:bg-slate-800 transition-colors ${costsRevealed ? "text-emerald-400" : "text-slate-400 hover:text-white"}`}>
                {costsRevealed ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>
            )}
          </div>

          <div className="relative flex-1 max-w-md mx-2 md:mx-4">
            {coordApp ? (
              <div className="hidden sm:flex items-center gap-2 text-xs text-slate-500 justify-center" data-testid="coord-app-header-title"><Radar className="w-4 h-4 text-sky-400" /><span className="font-semibold text-slate-300">Thoth Coordinamento</span> · {user?.company?.name}</div>
            ) : (<>
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              value={searchQ}
              onChange={(e) => { setSearchQ(e.target.value); setSearchOpen(true); }}
              onFocus={() => { setSearchOpen(true); loadVehicles(); }}
              onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
              placeholder="Cerca mezzo per targa..."
              data-testid="global-search-input"
              className="w-full bg-[#151C28] border border-slate-700 rounded-xl pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-blue-500"
            />
            {searchOpen && searchResults.length > 0 && (
              <div className="absolute left-0 right-0 mt-2 bg-[#151C28] border border-slate-700 rounded-xl shadow-2xl overflow-hidden z-50" data-testid="global-search-results">
                {searchResults.map((v) => (
                  <button key={v.id} onMouseDown={() => goVehicle(v.id)} data-testid={`global-search-result-${v.targa}`}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-800 text-left">
                    <Truck className="w-4 h-4 text-blue-400 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-plate font-semibold text-sm">{v.targa}</div>
                      <div className="text-[11px] text-slate-500 truncate">MEZZO {v.n_soc} · {v.tipo}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {searchOpen && searchQ.trim() && searchResults.length === 0 && (
              <div className="absolute left-0 right-0 mt-2 bg-[#151C28] border border-slate-700 rounded-xl shadow-2xl px-4 py-3 text-sm text-slate-500 z-50" data-testid="global-search-empty">
                Nessun mezzo trovato
              </div>
            )}
            </>)}
          </div>

          <div className="relative shrink-0">
            <button onClick={() => { setBellOpen((o) => !o); if (!bellOpen && unread) markAll(); }} data-testid="notifications-bell" className="relative p-2 rounded-lg hover:bg-slate-800">
              <Bell className="w-6 h-6" />
              {unread > 0 && <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-5 h-5 px-1 grid place-items-center" data-testid="notifications-count">{unread}</span>}
            </button>
            {bellOpen && (
              <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-[#151C28] border border-slate-700 rounded-xl shadow-2xl overflow-hidden z-40">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-800">
                  <span className="font-semibold text-sm">Notifiche</span>
                  <button onClick={clearAll} className="text-xs text-slate-400 hover:text-red-400 flex items-center gap-1" data-testid="clear-notifications-btn"><Trash2 className="w-3.5 h-3.5" /> Svuota</button>
                </div>
                <div className="max-h-96 overflow-y-auto divide-y divide-slate-800">
                  {notifs.length === 0 && <p className="text-slate-500 text-sm text-center py-8">Nessuna notifica</p>}
                  {notifs.map((n) => (
                    <div key={n.id} className="px-4 py-3 flex items-start gap-3 hover:bg-slate-800/40" data-testid={`notif-item-${n.id}`}>
                      <button onClick={() => { setBellOpen(false); navigate(n.url || "/admin/segnalazioni"); }} className="flex items-start gap-3 flex-1 min-w-0 text-left">
                        <AlertCircle className={`w-4 h-4 mt-0.5 shrink-0 ${n.gravita === "mezzo_inutilizzabile" ? "text-red-500" : n.gravita === "urgente" ? "text-amber-500" : "text-blue-400"}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm">{n.message}</p>
                          <p className="text-[11px] text-slate-500 mt-0.5">{new Date(n.created_at).toLocaleString("it-IT")}</p>
                        </div>
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); delNotif(n.id); }} className="text-slate-600 hover:text-red-400 shrink-0"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </header>
        </div>

        <main className="flex-1 p-4 md:p-8">
          {user?.company?.status === "trialing" && !user?.company?.expired && !user?.is_superadmin && (
            <div data-testid="trial-banner" className="mb-5 flex items-start gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-3">
              <CalendarClock className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-100 flex-1">
                <span className="font-semibold">Prova gratuita</span> — hai tutte le funzioni Pro, {user.company.days_left === 1 ? <>ti resta <b data-testid="trial-days-left">1</b> giorno</> : <>ti restano <b data-testid="trial-days-left">{user.company.days_left}</b> giorni</>}. Attiva l'abbonamento prima della scadenza per non interrompere il servizio.
                {isAdmin && <button onClick={() => navigate("/admin/abbonamento")} data-testid="trial-banner-subscribe-link" className="ml-2 underline font-semibold hover:text-white">Attiva ora</button>}
              </div>
            </div>
          )}
          {user?.company?.needs_attention && (
            <div data-testid="subscription-banner" className="mb-5 flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3">
              <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-100 flex-1">
                <><span className="font-semibold">Abbonamento da sistemare</span> — l'azienda <b>{user.company.name}</b> risulta «{user.company.status_label}».</>
                {isAdmin && !user?.is_superadmin && <button onClick={() => navigate("/admin/abbonamento")} data-testid="banner-subscribe-link" className="ml-2 underline font-semibold hover:text-white">Vai all'Abbonamento</button>}
              </div>
            </div>
          )}
          <Suspense fallback={
            <div className="flex items-center justify-center py-24" data-testid="admin-page-loader">
              <div className="w-9 h-9 rounded-full border-2 border-slate-700 border-t-blue-500 animate-spin" />
            </div>
          }>
          <Routes>
            {coordApp ? (<>
              <Route index element={<Navigate to={COORD_HOME} replace />} />
              <Route path="radar" element={(isAdmin || isCoord) ? (user?.company?.tier && user.company.tier !== "pro" ? <ProLock feature="Il Radar Voli" benefits={["Arrivi in tempo reale", "Voli lungo-raggio filtrati automaticamente", "Base per la pianificazione squadre"]} /> : <RadarVoli />) : <Navigate to="/admin/impostazioni" replace />} />
              <Route path="operai" element={(isAdmin || isCoord) ? (user?.company?.tier && user.company.tier !== "pro" ? <ProLock feature="La gestione Operai" benefits={["Anagrafica del personale operativo", "Turni e disponibilità", "Pronti per la pianificazione"]} /> : <Operai />) : <Navigate to={COORD_HOME} replace />} />
              <Route path="pianificazione" element={(isAdmin || isCoord) ? (user?.company?.tier && user.company.tier !== "pro" ? <ProLock feature="La pianificazione squadre" benefits={["Drag & drop sugli aerei", "Voli dal Radar", "Stampa del Check A4"]} /> : <Pianificazione />) : <Navigate to={COORD_HOME} replace />} />
              <Route path="turni" element={(isAdmin || isCoord) ? (user?.company?.tier && user.company.tier !== "pro" ? <ProLock feature="I Turni" benefits={["Carica la foto del brogliaccio", "L'AI estrae il personale in turno", "Operai disponibili nella Pianificazione"]} /> : <Turni />) : <Navigate to={COORD_HOME} replace />} />
              <Route path="impostazioni" element={<Impostazioni />} />
              <Route path="*" element={<Navigate to={COORD_HOME} replace />} />
            </>) : (<>
            <Route index element={isMagaz ? <Navigate to="/admin/segnalazioni" replace /> : isCoord ? <Navigate to="/admin/radar" replace /> : (user?.is_superadmin && !user?.impersonating ? <Navigate to="/admin/piattaforma" replace /> : <Dashboard />)} />
            <Route path="radar" element={(isAdmin || isCoord) ? (user?.company?.tier && user.company.tier !== "pro" ? <ProLock feature="Il Radar Voli" benefits={["Arrivi FCO in tempo reale", "Voli lungo-raggio filtrati automaticamente", "Base per la pianificazione squadre"]} /> : <RadarVoli />) : <Navigate to="/admin" replace />} />
            <Route path="operai" element={(isAdmin || isCoord) ? (user?.company?.tier && user.company.tier !== "pro" ? <ProLock feature="La gestione Operai" benefits={["Anagrafica del personale operativo", "Turni e disponibilità", "Pronti per la pianificazione"]} /> : <Operai />) : <Navigate to="/admin" replace />} />
            <Route path="pianificazione" element={(isAdmin || isCoord) ? (user?.company?.tier && user.company.tier !== "pro" ? <ProLock feature="La pianificazione squadre" benefits={["Drag & drop sugli aerei", "Voli dal Radar", "Stampa del Check A4"]} /> : <Pianificazione />) : <Navigate to="/admin" replace />} />
            <Route path="turni" element={(isAdmin || isCoord) ? (user?.company?.tier && user.company.tier !== "pro" ? <ProLock feature="I Turni" benefits={["Carica la foto del brogliaccio", "L'AI estrae il personale in turno", "Operai disponibili nella Pianificazione"]} /> : <Turni />) : <Navigate to="/admin" replace />} />
            <Route path="coordinatori" element={isAdmin ? (user?.company?.tier && user.company.tier !== "pro" ? <ProLock feature="I Coordinatori" benefits={["Crea account coordinatore", "Accesso dedicato al Radar Voli", "Gestione squadre e aerei"]} /> : <Coordinatori />) : <Navigate to="/admin/segnalazioni" replace />} />
            <Route path="richieste" element={canSeeTab(user, "segnalazioni") ? <Segnalazioni categoria="richiesta" onChange={() => { loadNotifs(); loadApproval(); }} /> : <Navigate to="/admin" replace />} />
            <Route path="segnalazioni" element={canSeeTab(user, "segnalazioni") ? <Segnalazioni categoria="segnalazione" onChange={() => { loadNotifs(); loadApproval(); }} /> : <Navigate to="/admin" replace />} />
            <Route path="da-approvare" element={isAdmin ? <DaApprovare onChange={() => { loadNotifs(); loadApproval(); }} /> : <Navigate to="/admin/segnalazioni" replace />} />
            <Route path="miei-interventi" element={isMagaz ? <MieiInterventi /> : <Navigate to="/admin" replace />} />
            <Route path="mezzi" element={canSeeTab(user, "mezzi") ? <Mezzi /> : <Navigate to="/admin" replace />} />
            <Route path="mezzi/:id" element={canSeeTab(user, "mezzi") ? <MezzoDetail /> : <Navigate to="/admin" replace />} />
            <Route path="appuntamenti" element={isAdmin ? <AppuntamentiBoard /> : <Navigate to="/admin/segnalazioni" replace />} />
            <Route path="costi" element={canSeeTab(user, "costi") ? (user?.company?.tier && user.company.tier !== "pro" ? <ProLock feature="La gestione Costi" benefits={["Costi per mezzo e per mese", "Scadenze e costi fissi", "Report e statistiche di spesa"]} /> : <Costi />) : <Navigate to="/admin" replace />} />
            <Route path="officine" element={canSeeTab(user, "officine") ? <Officine /> : <Navigate to="/admin" replace />} />
            <Route path="officine/:id" element={canSeeTab(user, "officine") ? <OfficinaDetail /> : <Navigate to="/admin" replace />} />
            <Route path="registro" element={canSeeTab(user, "registro") ? <Registro /> : <Navigate to="/admin" replace />} />
            <Route path="tipo-olio" element={canSeeTab(user, "tipo-olio") ? <TipoOlio /> : <Navigate to="/admin" replace />} />
            <Route path="magazzinieri" element={isAdmin ? <Magazzinieri /> : <Navigate to="/admin/segnalazioni" replace />} />
            <Route path="importa" element={isAdmin ? (user?.company?.tier && user.company.tier !== "pro" ? <ProLock feature="L'import dati" benefits={["Importa mezzi, interventi e scadenze da Excel/CSV", "Import massivo delle fatture", "Risparmi ore di inserimento manuale"]} /> : <ImportaDati />) : <Navigate to="/admin/segnalazioni" replace />} />
            <Route path="piattaforma" element={user?.is_superadmin ? <Aziende /> : <Navigate to="/admin" replace />} />
            <Route path="fatture" element={user?.is_superadmin ? <ArchivioFatture /> : <Navigate to="/admin/segnalazioni" replace />} />
            <Route path="admins" element={isAdmin ? <Admins /> : <Navigate to="/admin/segnalazioni" replace />} />
            <Route path="log" element={user?.role === "principal" ? (user?.company?.tier && user.company.tier !== "pro" ? <ProLock feature="Il Log attività" benefits={["Traccia tutte le azioni degli utenti", "Chi ha fatto cosa e quando", "Più sicurezza e controllo"]} /> : <AuditLog />) : <Navigate to="/admin" replace />} />
            <Route path="abbonamento" element={user?.role === "principal" ? <Abbonamento /> : <Navigate to="/admin" replace />} />
            <Route path="impostazioni" element={<Impostazioni />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
            </>)}
          </Routes>
          </Suspense>
        </main>
      </div>
    </div>
  );
}

function BlockedView({ company, onLogout }) {
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 flex flex-col" data-testid="company-blocked-view">
      <div className="border-b border-slate-800 px-5 h-16 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-700 grid place-items-center"><Zap className="w-5 h-5" fill="currentColor" /></div>
          <span className="font-head font-extrabold text-lg">Thoth</span>
        </div>
        <button onClick={onLogout} data-testid="blocked-logout-btn" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-2"><LogOut className="w-4 h-4" /> Esci</button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-amber-500/15 border border-amber-500/30 grid place-items-center mx-auto"><Lock className="w-8 h-8 text-amber-400" /></div>
            <h1 className="font-head font-extrabold text-2xl md:text-3xl mt-5" data-testid="blocked-title">{company?.status === "trialing" ? "Prova gratuita terminata" : "Abbonamento non attivo"}</h1>
            <p className="text-slate-400 mt-3 max-w-lg mx-auto">
              {company?.status === "trialing"
                ? <>I 3 giorni di prova di <b className="text-slate-200">{company?.name}</b> sono finiti. Attiva l'abbonamento per riprendere subito da dove eri: i tuoi dati sono al sicuro.</>
                : <>L'accesso a <b className="text-slate-200">{company?.name}</b> è sospeso ({company?.status_label}). Attiva un abbonamento per riprendere subito: i tuoi dati sono al sicuro.</>}
            </p>
          </div>
          <div className="mt-8"><SubscribePanel /></div>
          <p className="text-center text-xs text-slate-500 mt-6">Pagamenti sicuri con Stripe · Puoi disdire quando vuoi.</p>
        </div>
      </div>
    </div>
  );
}
